import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatAPI, createQueryKey } from '../services/api';

const usePaginatedMessages = (chatId, initialLimit = 30) => {
  // Helper: stable ascending order by createdAt, fallback to id as tiebreaker
  const sortByCreatedAtAsc = useCallback((messages) => {
    if (!Array.isArray(messages)) return [];
    const toTime = (m) => {
      try {
        const raw = (m && (m.createdAt || m.created_at || m.timestamp || m.created || m.created_at_ts)) || null;
        if (!raw) return 0;
        if (typeof raw === 'number') return raw;
        return new Date(raw).getTime() || 0;
      } catch (_) {
        return 0;
      }
    };
    return [...messages].sort((a, b) => {
      const ta = toTime(a);
      const tb = toTime(b);
      if (ta !== tb) return ta - tb;
      const ida = a?.id || '';
      const idb = b?.id || '';
      return ida.localeCompare(idb);
    });
  }, []);
  // Generate unique instance ID for debugging
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  
  // Simple message state management (removed Virtuoso complexity)
  const [allMessages, setAllMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [currentChatId, setCurrentChatId] = useState(chatId); // Track current chat for proper resets
  
  // Message limit to prevent memory issues (preserved from useVirtuosoMessages)
  const MAX_MESSAGES = 1000;
  
  // Helper to trim messages if they exceed the limit
  const sortAndTrim = useCallback((messages) => {
    const sorted = sortByCreatedAtAsc(messages);
    if (sorted.length > MAX_MESSAGES) {
      const excess = sorted.length - MAX_MESSAGES;
      return sorted.slice(excess); // Remove oldest messages
    }
    return sorted;
  }, [sortByCreatedAtAsc]);
  
  // Ref to track if we're currently loading initial messages
  const isInitialLoad = useRef(true);
  const queryClient = useQueryClient();
  
  // Fetch initial chat data with limited messages
  const { data: chatResponse, isLoading: isLoadingChat, error } = useQuery({
    queryKey: ['chat', chatId], // Simplified query key
    queryFn: () => {
      // Debug: chat fetch
      console.debug('usePaginatedMessages: [msgs] fetch chat', { chatId, initialLimit, instance: instanceId.current });
      // Always include messages explicitly with a larger window to reduce drop-offs
      return chatAPI.getChat(chatId, { include_messages: true, messages_limit: initialLimit * 2 });
    },
    enabled: !!chatId && chatId !== 'null',
    staleTime: 5000, // Optimized: Consider data fresh for 5s to reduce excessive refetching
    cacheTime: 300000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true, // ✅ STALE DATA FIX: Refetch when user returns to tab
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting chat
    refetchInterval: false, // Disable automatic refetching
    refetchOnReconnect: true, // ✅ STALE DATA FIX: Refetch on network reconnect
    // onSuccess: (data) => {
    //   console.log('✅ CHAT DATA FETCHED successfully:', data);
    // },
    onError: (error) => {
      console.error('[msgs] chat fetch failed', error);
      // Handle unauthorized access (403) - user trying to access chat from different project
      if (error?.response?.status === 403) {
        console.error('[msgs] Access denied: User does not have access to this chat');
        // Error will be handled by component using this hook
      } else if (error?.response?.status === 404) {
        console.error('[msgs] Chat not found (404)');
      }
    }
  });

  const chat = chatResponse?.data?.data?.chat;

  // Initialize messages when chat data loads
  useEffect(() => {
    // console.log('📥 PROCESSING CHAT DATA:', {
    //   chat: chat ? { id: chat.id, messageCount: chat.messages?.length || 0, totalMessageCount: chat.totalMessageCount } : null,
    //   currentChatId,
    //   allMessagesLength: allMessages.length,
    //   isInitialLoad: isInitialLoad.current
    // });
    
    // Only process chat data if it matches our current chat ID or expected chatId
    // ✅ FIX: Also accept chat data if it matches the expected chatId (handles race conditions)
    if (chat && (chat.id === currentChatId || chat.id === chatId)) {
      if (isInitialLoad.current) {
        console.debug('[msgs] initial load set', { count: chat.messages?.length || 0, total: chat.totalMessageCount });
        setAllMessages(sortAndTrim(chat.messages || []));
        setTotalMessageCount(chat.totalMessageCount || 0);
        
        // ✅ FIX: Determine if there are more messages by comparing counts
        // Use the backend's hasMoreMessages field if available, otherwise calculate
        const hasMore = chat.hasMoreMessages !== undefined 
          ? chat.hasMoreMessages 
          : (chat.totalMessageCount || 0) > (chat.messages?.length || 0);
        console.log('🔍 INITIAL LOAD - HasMore calculation:', {
          totalMessages: chat.totalMessageCount,
          currentMessages: chat.messages?.length,
          backendHasMore: chat.hasMoreMessages,
          calculatedHasMore: hasMore,
          chatData: {
            id: chat.id,
            totalMessageCount: chat.totalMessageCount,
            messagesLength: chat.messages?.length,
            hasMoreMessages: chat.hasMoreMessages
          }
        });
        
        // Force debug - let's see what's actually happening
        console.log('🔍 DEBUGGING hasMoreMessages:', {
          'chat.hasMoreMessages': chat.hasMoreMessages,
          'typeof chat.hasMoreMessages': typeof chat.hasMoreMessages,
          'chat.hasMoreMessages !== undefined': chat.hasMoreMessages !== undefined,
          'chat.totalMessageCount': chat.totalMessageCount,
          'chat.messages?.length': chat.messages?.length,
          'final hasMore value': hasMore
        });
        setHasMoreMessages(hasMore);
        isInitialLoad.current = false;
      } else if (chat.messages?.length > 0) {
        // Handle edge case where messages were cleared but we have new data for the same chat
        // OR when new messages arrive and we need to update
        console.debug('[msgs] refetch merge', { incoming: chat.messages?.length || 0, current: allMessages.length });
        
        // ✅ SIMPLIFIED MERGE: Just merge and sort by timestamp
        if (!isLoadingMore) {
          const map = new Map();
          // Seed existing messages
          for (const m of allMessages) {
            if (m && m.id) {
              map.set(m.id, m);
            }
          }
          
          // Merge incoming messages with reconciliation by id and client_message_id
          for (const m of (chat.messages || [])) {
            if (!m) continue;
            if (m.id) {
              const prevExisting = map.get(m.id);
              if (prevExisting) {
                // Update existing by id
                map.set(m.id, { ...m, _isOptimistic: prevExisting._isOptimistic || false });
                continue;
              }
            }
            if (m.client_message_id) {
              // Find any optimistic entry with the same client_message_id and role
              const existingOptimistic = Array.from(map.values()).find(x => 
                x && x.client_message_id && x.client_message_id === m.client_message_id && x.role === m.role
              );
              if (existingOptimistic) {
                // Replace the optimistic entry's key with real id (if present)
                if (m.id) {
                  map.delete(existingOptimistic.id || existingOptimistic.client_message_id);
                  map.set(m.id, { ...m, _isOptimistic: false });
                } else {
                  map.set(existingOptimistic.id || existingOptimistic.client_message_id, { ...m, _isOptimistic: false });
                }
                continue;
              }
            }
            // Add new message
            map.set(m.id || m.client_message_id, { ...m, _isOptimistic: false });
          }
          
          // Convert back to array, sort, and trim if needed
          const merged = Array.from(map.values());
          setAllMessages(sortAndTrim(merged));
          
          setTotalMessageCount(chat.totalMessageCount || 0);
          // ✅ FIX: Don't override hasMore during refetch merge - keep current pagination state
          // The refetch merge should only update messages, not pagination state
          // Pagination state is managed by loadMoreMessages and should not be overridden
          
          console.log('🔍 REFETCH MERGE - Skipping hasMore update to preserve pagination state:', {
            backendHasMore: chat.hasMoreMessages,
            totalMessages: chat.totalMessageCount,
            currentMessages: chat.messages?.length,
            currentHasMore: hasMoreMessages,
            'preserving current hasMore': hasMoreMessages
          });
          
          // Don't call setHasMoreMessages here - preserve current pagination state
        }
      }
    } else if (chat && chat.id !== currentChatId && chat.id !== chatId) {
      // ✅ FIX: Only warn if chat data doesn't match either currentChatId OR expected chatId
      // This indicates a true unexpected mismatch, not just a race condition
      console.warn('[msgs] chat id mismatch', { 
        chatDataId: chat.id, 
        currentChatId, 
        expectedChatId: chatId 
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat, currentChatId, isLoadingMore, chatId, sortAndTrim]);

  // Handle chatId changes gracefully - don't clear messages unnecessarily
  useEffect(() => {
    if (!chatId) {
      // Do not clear existing messages on transient null/undefined chatId
      return;
    }
    if (chatId !== currentChatId) {
      console.debug('[msgs] chat id changed', { from: currentChatId, to: chatId, currentCount: allMessages.length });
      setCurrentChatId(chatId);
      
      // DON'T clear messages - let the new chat data merge in naturally
      // This prevents welcome messages from disappearing during chat transitions
      // The merge logic in the chat data effect will handle deduplication
      
      // Reset pagination state but preserve messages
      setCurrentPage(1);
      setHasMoreMessages(false);
      setIsLoadingMore(false);
      setTotalMessageCount(0);
      isInitialLoad.current = true;
      
      // Only invalidate if we're switching between two valid chat IDs
      // Don't invalidate on initial null -> chatId transition to preserve welcome message
      if (currentChatId && chatId) {
        queryClient.invalidateQueries({ queryKey: createQueryKey.chat(chatId) });
      }
    }
  }, [chatId, currentChatId, queryClient, allMessages.length]);

  // Load more messages function
  const loadMoreMessages = useCallback(async (containerRef) => {
    if (isLoadingMore || !hasMoreMessages || !chatId) {
      console.debug('[msgs] load more blocked', { isLoadingMore, hasMoreMessages, chatId });
      return;
    }

    console.debug('[msgs] load more', { page: currentPage + 1, current: allMessages.length });
    setIsLoadingMore(true);
    
    try {
      // Store current scroll position and height
      const container = containerRef?.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      const previousScrollTop = container?.scrollTop || 0;

      const response = await chatAPI.getChatMessages(chatId, {
        page: currentPage + 1,
        limit: initialLimit
      });

      console.debug('[msgs] pagination response', { len: response?.data?.data?.messages?.length });
      const newMessagesData = response?.data?.data;
      
      if (newMessagesData?.messages && newMessagesData.messages.length > 0) {
        // Reverse messages since they come in descending order (newest first)
        const newMessages = [...newMessagesData.messages].reverse();
        console.debug('[msgs] prepend count', { prepend: newMessages.length });
        
      // Prepend new messages to existing ones with de-duplication by id
      const existingIds = new Set(allMessages.map(m => m && m.id).filter(Boolean));
      const dedupedNew = newMessages.filter(m => !m?.id || !existingIds.has(m.id));
      const combined = [...dedupedNew, ...allMessages];
      // Ensure ascending order overall and trim if needed (defensive)
      setAllMessages(sortAndTrim(combined));
        
        setCurrentPage(prev => prev + 1);
        
        // ✅ FIX: Update hasMoreMessages based on response OR calculate from message counts
        let hasMore = false;
        if (newMessagesData.pagination?.hasNext !== undefined) {
          // If backend provides pagination info, use it
          hasMore = newMessagesData.pagination.hasNext;
        } else if (newMessagesData.pagination?.total) {
          // Otherwise calculate based on total count vs current count
          const newTotal = allMessages.length + newMessages.length;
          hasMore = newTotal < newMessagesData.pagination.total;
        } else {
          // Fallback: if we got fewer messages than requested, probably no more
          hasMore = newMessages.length >= initialLimit;
        }
        
        console.log('🔍 PAGINATION - HasMore calculation:', {
          paginationHasNext: newMessagesData.pagination?.hasNext,
          paginationTotal: newMessagesData.pagination?.total,
          currentTotal: allMessages.length + newMessages.length,
          requestedLimit: initialLimit,
          receivedCount: newMessages.length,
          calculatedHasMore: hasMore
        });
        
        // console.log('🔍 PAGINATION - HasMore calculation:', {
        //   paginationHasNext: newMessagesData.pagination?.hasNext,
        //   totalMessageCount: newMessagesData.totalMessageCount,
        //   currentTotal: allMessages.length + newMessages.length,
        //   requestedLimit: initialLimit,
        //   receivedCount: newMessages.length,
        //   calculatedHasMore: hasMore
        // });
        
        setHasMoreMessages(hasMore);

        // Smart scroll position management
        if (container) {
          // Wait for DOM to update
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeight;
            
            // Maintain relative scroll position
            container.scrollTop = previousScrollTop + heightDifference;
          }, 50);
        }
      } else {
        // No more messages received
        console.debug('[msgs] no more to load');
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('❌ Error loading more messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, currentPage, hasMoreMessages, isLoadingMore, initialLimit, sortAndTrim]);

  // Add new message (for real-time updates) - Comprehensive deduplication (preserved from useVirtuosoMessages)
  const addMessage = useCallback((newMessage) => {
    if (!newMessage) return;
    
    setAllMessages(prev => {
      // Check for duplicates by ID first
      if (newMessage.id) {
        const existingIndex = prev.findIndex(msg => msg.id === newMessage.id);
        if (existingIndex !== -1) {
          // Update existing message instead of adding duplicate
          const updated = [...prev];
          updated[existingIndex] = newMessage;
          return sortAndTrim(updated);
        }
      }
      
      // Check for duplicates by client_message_id (for optimistic messages)
      if (newMessage.client_message_id) {
        const existingIndex = prev.findIndex(msg => 
          msg.client_message_id === newMessage.client_message_id && 
          msg.role === newMessage.role
        );
        if (existingIndex !== -1) {
          // Replace optimistic message with real one
          const updated = [...prev];
          updated[existingIndex] = newMessage;
          return sortAndTrim(updated);
        }
      }
      
      // Prevent duplicate assistant messages (welcome message protection)
      if (newMessage.role === 'assistant') {
        const duplicateAssistant = prev.find(msg => 
          msg.role === 'assistant' && 
          msg.content === newMessage.content &&
          Math.abs(new Date(msg.createdAt || 0).getTime() - new Date(newMessage.createdAt || 0).getTime()) < 5000
        );
        if (duplicateAssistant) {
          // Don't add duplicate assistant message
          console.debug('[msgs] duplicate assistant message blocked', { 
            newId: newMessage.id, 
            existingId: duplicateAssistant.id,
            content: newMessage.content?.substring(0, 50) + '...'
          });
          return prev; // Return unchanged array
        }
      }
      
      // Add new message, sort, and trim if needed
      const updated = [...prev, newMessage];
      return sortAndTrim(updated);
    });
    
    setTotalMessageCount(prev => prev + 1);
  }, [sortAndTrim]);

  // Update message (for streaming updates) - Supports both id and client_message_id (preserved from useVirtuosoMessages)
  const updateMessage = useCallback((messageId, updates) => {
    if (!messageId || !updates) return;
    
    setAllMessages(prev => {
      const index = prev.findIndex(m => 
        (m.id === messageId || m.client_message_id === messageId)
      );
      if (index === -1) return prev;
      
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  }, []);

  return {
    // Server state (persisted messages)
    messages: allMessages,
    isLoading: isLoadingChat,
    isLoadingMore,
    hasMoreMessages,
    totalMessageCount,
    loadMoreMessages,
    error,
    
    // Client state actions
    addMessage,
    updateMessage,
    
    // Legacy compatibility
    hasMore: hasMoreMessages,
    loadMore: loadMoreMessages,
    chat: chat ? {
      ...chat,
      messages: allMessages,
      totalMessageCount,
      hasMoreMessages
    } : null
  };
};

export default usePaginatedMessages; 